import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Layout } from 'antd';
import {
  MailOutlined,
  FundFilled,
  ShopFilled,
  ReadFilled,
  RocketFilled,
  MessageFilled,
  ContactsFilled,
  CrownFilled,
  AppstoreFilled
} from '@ant-design/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useStore } from '../../../store/useStore';
import { getAccountMenus, getCurrentAccount } from '../../../store/request';
import type { CurrentAccount, VisibleMenu } from '../../../types/account';
import { useTranslation } from '../../../i18n';
import { CurrentAccountProvider } from './currentAccountContext';
import { MerchantRealtimeProvider } from './merchantRealtimeContext';
import { SelectedShopProvider } from './selectedShopContext';
import { MainSider, type AppMenuItem, type MenuIconTone } from './MainSider';
import { MainHeader } from './MainHeader';

type MenuIconMeta = { icon: React.ReactNode; tone: MenuIconTone };

const MENU_ICON_BY_ROUTE: Record<string, MenuIconMeta> = {
  '/creator': { icon: <FundFilled />, tone: 'violet' },
  '/creator/store_authorization': { icon: <ShopFilled />, tone: 'emerald' },
  '/creator/talent_square': { icon: <CrownFilled />, tone: 'cyan' },
  '/creator/creator_manager': { icon: <ContactsFilled />, tone: 'indigo' },
  '/creator/invitation': { icon: <RocketFilled />, tone: 'rose' },
  '/creator/private_message': { icon: <MessageFilled />, tone: 'sky' },
  '/creator/email': { icon: <MailOutlined />, tone: 'amber' },
  '/creator/script': { icon: <ReadFilled />, tone: 'blue' }
};

const MENU_ICON_BY_COMPONENT: Record<string, MenuIconMeta> = {
  WORKBENCH: MENU_ICON_BY_ROUTE['/creator'],
  STORE_AUTHORIZATION: MENU_ICON_BY_ROUTE['/creator/store_authorization'],
  TALENT_SQUARE: MENU_ICON_BY_ROUTE['/creator/talent_square'],
  CREATOR_MANAGER: MENU_ICON_BY_ROUTE['/creator/creator_manager'],
  INVITATION: MENU_ICON_BY_ROUTE['/creator/invitation'],
  PRIVATE_MESSAGE: MENU_ICON_BY_ROUTE['/creator/private_message'],
  EMAIL: MENU_ICON_BY_ROUTE['/creator/email'],
  SCRIPT: MENU_ICON_BY_ROUTE['/creator/script']
};

function resolveMenuIcon(node: VisibleMenu): MenuIconMeta {
  const routePath = node.routePath?.trim() ?? '';
  if (routePath && MENU_ICON_BY_ROUTE[routePath]) return MENU_ICON_BY_ROUTE[routePath];
  const componentKey = node.componentKey?.trim().toUpperCase() ?? '';
  if (componentKey && MENU_ICON_BY_COMPONENT[componentKey]) return MENU_ICON_BY_COMPONENT[componentKey];
  return { icon: <AppstoreFilled />, tone: 'violet' };
}

function visibleChildrenOf(node: VisibleMenu): VisibleMenu[] {
  return Array.isArray(node.children) ? node.children.filter(Boolean) : [];
}

function sortVisibleMenus(nodes: VisibleMenu[]): VisibleMenu[] {
  return [...nodes].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** 有子节点 → 目录；否则有 routePath → 可跳转叶子（含空 children 的 DIRECTORY）。 */
function toAppMenuItems(nodes: VisibleMenu[], parentKey = 'menu'): AppMenuItem[] {
  return sortVisibleMenus(nodes).flatMap((node, index) => {
    const routePath = node.routePath?.trim() ?? '';
    const childNodes = visibleChildrenOf(node);
    const key = routePath || `${parentKey}/${index}`;
    const label = node.displayName?.trim() || key;
    const iconMeta = resolveMenuIcon(node);

    if (childNodes.length > 0) {
      const children = toAppMenuItems(childNodes, key);
      if (children.length === 0) {
        if (!routePath) return [];
        return [{ key: routePath, label, icon: iconMeta.icon, iconTone: iconMeta.tone }];
      }
      return [
        {
          key,
          label,
          icon: iconMeta.icon,
          iconTone: iconMeta.tone,
          children
        }
      ];
    }

    if (!routePath) return [];
    return [{ key: routePath, label, icon: iconMeta.icon, iconTone: iconMeta.tone }];
  });
}

function flattenLeaves(items: AppMenuItem[]): AppMenuItem[] {
  const leaves: AppMenuItem[] = [];
  items.forEach((item) => {
    if (item.children?.length) leaves.push(...flattenLeaves(item.children));
    else leaves.push(item);
  });
  return leaves;
}

function findMenuMatch(items: AppMenuItem[], path: string): { selectedKey: string; openKeys: string[] } | null {
  for (const item of items) {
    if (item.children?.length) {
      const nested = findMenuMatch(item.children, path);
      if (nested) return { selectedKey: nested.selectedKey, openKeys: [item.key, ...nested.openKeys] };
      continue;
    }
    if (item.key === path) return { selectedKey: item.key, openKeys: [] };
    if (item.key !== '/creator' && path.startsWith(`${item.key}/`)) {
      return { selectedKey: item.key, openKeys: [] };
    }
  }
  return null;
}

function fallbackMenuItems(t: (key: string) => string): AppMenuItem[] {
  return [
    {
      key: '/creator',
      icon: <FundFilled />,
      iconTone: 'violet',
      label: t('layout.menu_workbench')
    },
    {
      key: '/creator/store_authorization',
      icon: <ShopFilled />,
      iconTone: 'emerald',
      label: t('layout.menu_store')
    },
    {
      key: '/creator/talent_square',
      icon: <CrownFilled />,
      iconTone: 'cyan',
      label: t('layout.menu_talent')
    },
    {
      key: '/creator/creator_manager',
      icon: <ContactsFilled />,
      iconTone: 'indigo',
      label: t('layout.menu_creator')
    },
    {
      key: '/creator/invitation',
      icon: <RocketFilled />,
      iconTone: 'rose',
      label: t('layout.menu_invitation')
    },
    {
      key: '/creator/private_message',
      icon: <MessageFilled />,
      iconTone: 'sky',
      label: t('layout.menu_dm')
    },
    {
      key: '/creator/email',
      icon: <MailOutlined />,
      iconTone: 'amber',
      label: t('layout.menu_email')
    },
    {
      key: '/creator/script',
      icon: <ReadFilled />,
      iconTone: 'blue',
      label: t('layout.menu_script')
    }
  ];
}

export const MerchantLayout = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectKeys, setSelectKeys] = useState(['/creator']);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const { loginStore } = useStore();
  const { t, i18n } = useTranslation();
  const [account, setAccount] = useState<CurrentAccount | null>(null);
  const [menuItems, setMenuItems] = useState<AppMenuItem[]>(() => fallbackMenuItems(t));
  const { Content } = Layout;

  const logout = () => {
    loginStore.logout();
    navigate('/creator/login');
  };

  const staticFallback = useMemo(() => fallbackMenuItems(t), [t]);

  useEffect(() => {
    let cancelled = false;
    getCurrentAccount()
      .then((res) => {
        if (!cancelled && res.code === 200 && res.content) setAccount(res.content);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  useEffect(() => {
    let cancelled = false;
    getAccountMenus()
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !Array.isArray(res.content) || res.content.length === 0) {
          setMenuItems(staticFallback);
          return;
        }
        const next = toAppMenuItems(res.content);
        setMenuItems(next.length > 0 ? next : staticFallback);
      })
      .catch(() => {
        if (!cancelled) setMenuItems(staticFallback);
      });
    return () => {
      cancelled = true;
    };
  }, [staticFallback]);

  useEffect(() => {
    const matched = findMenuMatch(menuItems, location.pathname);
    if (!matched) {
      const leaves = flattenLeaves(menuItems);
      const leaf =
        leaves.find((item) => item.key === location.pathname) ??
        leaves.find((item) => item.key !== '/creator' && location.pathname.startsWith(`${item.key}/`));
      setSelectKeys(leaf ? [leaf.key] : []);
      return;
    }
    setSelectKeys([matched.selectedKey]);
    if (matched.openKeys.length > 0) {
      setOpenKeys((prev) => Array.from(new Set([...prev, ...matched.openKeys])));
    }
  }, [location.pathname, menuItems]);

  const userDisplayName = account?.displayName?.trim() || loginStore.data.userinfo?.userName?.trim() || 'wowbit';

  return (
    <CurrentAccountProvider account={account}>
      <SelectedShopProvider>
        <MerchantRealtimeProvider token={loginStore.data.userinfo?.token}>
          <Layout className='main-app-layout main-shell'>
          <MainHeader userDisplayName={userDisplayName} logout={logout} />
          <Layout className='main-app-layout main-body-layout'>
            <MainSider
              collapsed={false}
              items={menuItems}
              selectKeys={selectKeys}
              openKeys={openKeys}
              onMenuSelect={(key) => navigate(key)}
              onOpenChange={setOpenKeys}
            />
            <Content className='main-content'>
              <Outlet />
            </Content>
          </Layout>
          </Layout>
        </MerchantRealtimeProvider>
      </SelectedShopProvider>
    </CurrentAccountProvider>
  );
});
